import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, FileCheck, Loader, User, ArrowLeft, Save, Plus, Trash } from 'lucide-react';
import { useClients } from '../context/ClientsContext';
import { useDocuments } from '../context/DocumentsContext';
import { useAuth } from '../context/AuthContext';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import Input from '../components/UI/Input';
import Select from '../components/UI/Select';
import TextArea from '../components/UI/TextArea';
import type { Document } from '../context/DocumentsContext';

type DocumentType = Document['type'];

interface LineItem {
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

const CreateDocument: React.FC = () => {
  const [type, setType] = useState<DocumentType>('invoice');
  const [client_id, setClientId] = useState<string>('');
  const [date, setDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [dueDate, setDueDate] = useState<string>('');
  const [prompt, setPrompt] = useState<string>('');
  const [amount, setAmount] = useState<string>('');
  const [vat, setVat] = useState<boolean>(true);
  const [vatRate] = useState<number>(20);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [step, setStep] = useState<number>(1);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [generatedDocument, setGeneratedDocument] = useState<Document | null>(null);
  const [content, setContent] = useState<string>('');
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  
  const { clients, getClient } = useClients();
  const { addDocument, updateDocument } = useDocuments();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  useEffect(() => {
    if (type === 'invoice') {
      const today = new Date();
      const futureDate = new Date(today.setDate(today.getDate() + 15));
      setDueDate(futureDate.toISOString().split('T')[0]);
    } else {
      setDueDate('');
    }
  }, [type]);

  const handleLineItemChange = (index: number, field: keyof LineItem, value: string) => {
    const newLineItems = [...lineItems];
    const item = { ...newLineItems[index] };

    if (field === 'description') {
      item.description = value;
    } else {
      const numValue = parseFloat(value) || 0;
      if (field === 'quantity' || field === 'unitPrice') {
        item[field] = numValue;
        item.total = item.quantity * item.unitPrice;
      }
    }

    newLineItems[index] = item;
    setLineItems(newLineItems);
    updateTotalAmount(newLineItems);
  };

  const addLineItem = () => {
    setLineItems([...lineItems, {
      description: '',
      quantity: 1,
      unitPrice: 0,
      total: 0
    }]);
  };

  const removeLineItem = (index: number) => {
    if (lineItems.length > 1) {
      const newLineItems = lineItems.filter((_, i) => i !== index);
      setLineItems(newLineItems);
      updateTotalAmount(newLineItems);
    }
  };

  const updateTotalAmount = (items: LineItem[]) => {
    const total = items.reduce((sum, item) => sum + item.total, 0);
    setAmount(total.toString());
  };
  
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!client_id) newErrors.client_id = 'Veuillez sélectionner un client';
    if (!date) newErrors.date = 'Veuillez sélectionner une date';
    if (type === 'invoice' && !dueDate) newErrors.dueDate = 'Veuillez sélectionner une date d\'échéance';
    if (!prompt) newErrors.prompt = 'Veuillez entrer une description pour la génération du document';
    if (amount && (isNaN(parseFloat(amount)) || parseFloat(amount) <= 0)) {
      newErrors.amount = 'Veuillez entrer un montant valide';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleGenerate = async () => {
    if (!validate()) return;
    
    setIsGenerating(true);
    setErrors({});
    
    try {
      const client = clients.find(c => c.id === client_id);
      if (!client) throw new Error('Client not found');

      // Add timeout to prevent infinite loading
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-document`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type,
          prompt,
          amount: amount ? parseFloat(amount) : undefined,
          clientName: client.name,
          dueDate: type === 'invoice' ? dueDate : undefined,
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate document content');
      }

      const data = await response.json();
      
      if (!data || data.error) {
        throw new Error(data?.error || 'Failed to generate document content');
      }
      
      setLineItems(data.lineItems || []);
      setContent(data.content || '');
      if (!amount && data.totalAmount) {
        setAmount(data.totalAmount.toString());
      }

      // Create document with generated content
      const newDocument = await addDocument({
        type,
        client_id,
        date,
        due_date: type === 'invoice' ? dueDate : undefined,
        prompt,
        amount: amount ? parseFloat(amount) : data.totalAmount,
        vat,
        vat_rate: vatRate,
        status: 'draft',
        line_items: data.lineItems,
      });
      
      setGeneratedDocument(newDocument);
      setStep(3);
    } catch (error) {
      console.error('Erreur lors de la création du document:', error);
      setErrors(prev => ({
        ...prev,
        generation: error instanceof Error ? error.message : 'Une erreur est survenue lors de la génération du document',
      }));
      setStep(1);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!generatedDocument) return;

    try {
      setIsSaving(true);
      await updateDocument(generatedDocument.id, {
        content,
        amount: parseFloat(amount),
        line_items: lineItems,
      });
      navigate(`/app/documents/${generatedDocument.id}`);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du document:', error);
      setErrors(prev => ({
        ...prev,
        save: 'Une erreur est survenue lors de la sauvegarde du document',
      }));
    } finally {
      setIsSaving(false);
    }
  };

  const calculateTotal = () => {
    const baseAmount = parseFloat(amount) || 0;
    if (!vat) return baseAmount;
    const vatAmount = baseAmount * (vatRate / 100);
    return baseAmount + vatAmount;
  };

  const client = client_id ? getClient(client_id) : null;
  
  return (
    <div className="max-w-4xl mx-auto animate-fade-in">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
        Créer un Nouveau Document
      </h1>
      
      <div className="mb-8">
        <div className="flex flex-col md:flex-row">
          <div className="flex items-center mb-4 md:mb-0 md:mr-8">
            <div 
              className={`
                flex items-center justify-center w-8 h-8 rounded-full 
                ${step >= 1 ? 'bg-primary-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}
              `}
            >
              1
            </div>
            <span className="ml-2 font-medium">Informations du Document</span>
            <div className="ml-2 h-1 w-8 bg-gray-200 dark:bg-gray-700 md:hidden"></div>
          </div>
          
          <div className="hidden md:block h-1 w-16 mt-4 bg-gray-200 dark:bg-gray-700"></div>
          
          <div className="flex items-center mb-4 md:mb-0 md:mr-8">
            <div 
              className={`
                flex items-center justify-center w-8 h-8 rounded-full 
                ${step >= 2 ? 'bg-primary-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}
              `}
            >
              2
            </div>
            <span className="ml-2 font-medium">Vérification</span>
            <div className="ml-2 h-1 w-8 bg-gray-200 dark:bg-gray-700 md:hidden"></div>
          </div>

          <div className="hidden md:block h-1 w-16 mt-4 bg-gray-200 dark:bg-gray-700"></div>
          
          <div className="flex items-center">
            <div 
              className={`
                flex items-center justify-center w-8 h-8 rounded-full 
                ${step >= 3 ? 'bg-primary-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}
              `}
            >
              3
            </div>
            <span className="ml-2 font-medium">Édition & Finalisation</span>
          </div>
        </div>
      </div>
      
      {step === 1 && (
        <Card>
          <Card.Header 
            title="Informations du Document"
            subtitle="Remplissez les détails de votre nouveau document"
          />
          
          <Card.Content className="space-y-6">
            <div className="flex flex-col md:flex-row gap-6">
              <div className="w-full md:w-1/2">
                <Select
                  label="Type de Document"
                  value={type}
                  onChange={(e) => setType(e.target.value as DocumentType)}
                  options={[
                    { value: 'invoice', label: 'Facture' },
                    { value: 'quote', label: 'Devis' },
                  ]}
                  required
                />
              </div>
              
              <div className="w-full md:w-1/2">
                <Select
                  label="Client"
                  value={client_id}
                  onChange={(e) => setClientId(e.target.value)}
                  options={clients.map(client => ({
                    value: client.id,
                    label: client.name + (client.company ? ` (${client.company})` : ''),
                  }))}
                  placeholder="Sélectionner un client"
                  required
                  error={errors.client_id}
                />
              </div>
            </div>
            
            <div className="flex flex-col md:flex-row gap-6">
              <div className="w-full md:w-1/2">
                <Input
                  label="Date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  required
                  error={errors.date}
                />
              </div>
              
              {type === 'invoice' && (
                <div className="w-full md:w-1/2">
                  <Input
                    label="Date d'échéance"
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    required
                    error={errors.dueDate}
                  />
                </div>
              )}
            </div>
            
            <TextArea
              label="Description"
              placeholder="Décrivez le travail à facturer. Par exemple : 'Développement de site web incluant design responsive, fonctionnalités e-commerce et optimisation SEO.'"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={4}
              required
              error={errors.prompt}
            />

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="vat"
                checked={vat}
                onChange={(e) => setVat(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="vat" className="text-sm text-gray-700 dark:text-gray-300">
                Appliquer la TVA ({vatRate}%)
              </label>
            </div>

            {errors.generation && (
              <div className="bg-error-50 dark:bg-error-900/20 p-3 rounded-md border border-error-200 dark:border-error-800">
                <p className="text-error-600 dark:text-error-400 text-sm">{errors.generation}</p>
              </div>
            )}
          </Card.Content>
          
          <Card.Footer className="flex justify-end">
            <Button 
              onClick={() => {
                if (validate()) {
                  setStep(2);
                }
              }}
            >
              Continuer
            </Button>
          </Card.Footer>
        </Card>
      )}
      
      {step === 2 && (
        <div className="space-y-6">
          <Card>
            <Card.Header 
              title="Vérification des Informations"
              subtitle="Confirmez les détails de votre document avant la génération"
            />
            
            <Card.Content className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Type de Document</h3>
                  <p>{type === 'invoice' ? 'Facture' : 'Devis'}</p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Client</h3>
                  <div className="flex items-center">
                    <div className="h-6 w-6 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center text-primary-700 dark:text-primary-300 mr-2">
                      <User className="h-3 w-3" />
                    </div>
                    <p>{clients.find(c => c.id === client_id)?.name || 'Client Inconnu'}</p>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Date</h3>
                  <p>{new Date(date).toLocaleDateString()}</p>
                </div>
                
                {type === 'invoice' && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Date d'échéance</h3>
                    <p>{new Date(dueDate).toLocaleDateString()}</p>
                  </div>
                )}
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Description</h3>
                <p className="text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-800 p-3 rounded-md border border-gray-200 dark:border-gray-700">{prompt}</p>
              </div>
            </Card.Content>
            
            <Card.Footer className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
              <Button 
                variant="outline" 
                onClick={() => setStep(1)}
                disabled={isGenerating}
              >
                Retour
              </Button>
              
              <Button 
                onClick={handleGenerate}
                isLoading={isGenerating}
                disabled={isGenerating}
              >
                {isGenerating ? (
                  <div className="flex items-center">
                    <div className="animate-spin mr-2">
                      <Loader className="h-5 w-5" />
                    </div>
                    <div className="flex flex-col items-start">
                      <span>Génération en cours...</span>
                      <span className="text-xs opacity-75">L'IA rédige votre document</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <FileCheck className="h-4 w-4 mr-2" />
                    Générer le Document
                  </>
                )}
              </Button>
            </Card.Footer>
          </Card>
        </div>
      )}

      {step === 3 && generatedDocument && client && user && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-md">
            <div className="p-8">
              <div className="flex justify-between items-start mb-8">
                <div className="flex items-center">
                  {user?.logo ? (
                    <img 
                      src={user.logo} 
                      alt="Logo de l'entreprise" 
                      className="h-16 w-auto object-contain mr-4"
                    />
                  ) : (
                    <h2 className="text-2xl font-bold text-primary-600 dark:text-primary-400">KreaInvoice</h2>
                  )}
                  <div>
                    <p className="text-gray-600 dark:text-gray-400">Générateur de Documents</p>
                  </div>
                </div>
                <div className="text-right">
                  <h3 className="text-xl font-bold">{type === 'invoice' ? 'FACTURE' : 'DEVIS'}</h3>
                  <p>#{generatedDocument.number}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-8 mb-8">
                <div>
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">De</h4>
                  <p className="font-medium">{user.company || user.name}</p>
                  <p>123 Rue de Paris</p>
                  <p>75001 Paris</p>
                  <p>contact@votreentreprise.fr</p>
                  <p>01 23 45 67 89</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Facturer à</h4>
                  <p className="font-medium">{client.name}</p>
                  <p>{client.company}</p>
                  <p>{client.address}</p>
                  <p>{client.email}</p>
                  <p>{client.phone}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-8 mb-8">
                <div>
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                    Date {type === 'invoice' ? 'de facture' : 'du devis'}
                  </h4>
                  <p>{new Date(date).toLocaleDateString()}</p>
                </div>
                <div>
                  {type === 'invoice' && dueDate && (
                    <>
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Date d'échéance</h4>
                      <p>{new Date(dueDate).toLocaleDateString()}</p>
                    </>
                  )}
                </div>
              </div>
              
              <div className="mb-8">
                <div className="bg-gray-50 dark:bg-gray-700 rounded-t-lg border border-gray-200 dark:border-gray-600 px-4 py-3">
                  <div className="grid grid-cols-12 gap-4">
                    <div className="col-span-6">
                      <h4 className="text-sm font-medium">Description</h4>
                    </div>
                    <div className="col-span-2 text-right">
                      <h4 className="text-sm font-medium">Quantité</h4>
                    </div>
                    <div className="col-span-2 text-right">
                      <h4 className="text-sm font-medium">Prix unitaire</h4>
                    </div>
                    <div className="col-span-2 text-right">
                      <h4 className="text-sm font-medium">Total</h4>
                    </div>
                  </div>
                </div>
                
                <div className="border-x border-b border-gray-200 dark:border-gray-600 rounded-b-lg">
                  {lineItems.map((item, index) => (
                    <div key={index} className="px-4 py-3 border-b border-gray-200 dark:border-gray-600">
                      <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-6">
                          <Input
                            value={item.description}
                            onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                            placeholder="Description"
                          />
                        </div>
                        <div className="col-span-2">
                          <Input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => handleLineItemChange(index, 'quantity', e.target.value)}
                            className="text-right"
                            min="1"
                          />
                        </div>
                        <div className="col-span-2">
                          <Input
                            type="number"
                            value={item.unitPrice}
                            onChange={(e) => handleLineItemChange(index, 'unitPrice', e.target.value)}
                            className="text-right"
                            min="0"
                            step="0.01"
                          />
                        </div>
                        <div className="col-span-2 text-right flex items-center justify-end">
                          <span className="mr-2">{item.total.toLocaleString()} €</span>
                          {lineItems.length > 1 && (
                            <button
                              onClick={() => removeLineItem(index)}
                              className="text-error-500 hover:text-error-600 dark:text-error-400 dark:hover:text-error-300"
                            >
                              <Trash className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  <div className="px-4 py-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={addLineItem}
                      leftIcon={<Plus className="h-4 w-4" />}
                    >
                      Ajouter une ligne
                    </Button>
                  </div>
                  
                  <div className="px-4 py-4">
                    <div className="flex justify-end">
                      <div className="w-48">
                        <div className="flex justify-between py-1">
                          <p className="text-gray-600 dark:text-gray-400">Montant HT</p>
                          <p>{parseFloat(amount).toLocaleString()} €</p>
                        </div>
                        {vat && (
                          <div className="flex justify-between py-1">
                            <p className="text-gray-600 dark:text-gray-400">TVA ({vatRate}%)</p>
                            <p>{(parseFloat(amount) * vatRate / 100).toLocaleString()} €</p>
                          </div>
                        )}
                        <div className="flex justify-between py-1 font-medium border-t border-gray-200 dark:border-gray-700 mt-2 pt-2">
                          <p>Total {vat ? 'TTC' : 'HT'}</p>
                          <p>{calculateTotal().toLocaleString()} €</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mb-8">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Notes</h4>
                <TextArea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={6}
                />
              </div>
              
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4 text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Merci pour votre confiance !
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-between">
            <Button 
              variant="outline"
              leftIcon={<ArrowLeft className="h-4 w-4" />}
              onClick={() => setStep(2)}
            >
              Retour
            </Button>
            
            <Button
              leftIcon={<Save className="h-4 w-4" />}
              onClick={handleSave}
              isLoading={isSaving}
            >
              Enregistrer
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateDocument;